import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { handleErrorsUntilSignedOutOnError, LinkAccountState, SessionSet, SessionState, SignOutOnError } from '@platform-frontend/account';
import { CheckoutSession } from '@platform-frontend/checkout-drop-in';
import { MARKETPLACE_ERROR_HANDLERS } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { convertToHttpParams, handleErrors } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { VendorStateModel } from './model/vendor-state-model';
import { SetCheckoutSessionRequired } from './vendor.actions';

@State<VendorStateModel>({
  name: 'vendor',
  defaults: {
    checkoutSession: null,
    checkoutSessionRequired: false
  }
})
@Injectable()
export class VendorState {
  constructor(private actions$: Actions, private http: HttpClient, private store: Store) {}

  isDisabled$: Observable<boolean | null> = this.store.select(SessionState.isDisabled);
  linkedAccountId$: Observable<string | null> = this.store.select(LinkAccountState.accountId);

  @Selector()
  static checkoutSession(state: VendorStateModel): CheckoutSession | null {
    return state.checkoutSession;
  }

  @Action(SessionSet)
  sessionSet(ctx: StateContext<VendorStateModel>, { isDisabled }: SessionSet): Observable<unknown> {
    return this.fetchCheckoutSession(ctx, isDisabled);
  }

  @Action(SetCheckoutSessionRequired)
  setCheckoutSessionRequired(ctx: StateContext<VendorStateModel>): Observable<unknown> {
    const checkoutSessionAlreadyRequired = ctx.getState().checkoutSessionRequired;
    ctx.patchState({ checkoutSessionRequired: true });
    return checkoutSessionAlreadyRequired
      ? of(null)
      : this.isDisabled$.pipe(
          take(1),
          switchMap(isDisabled => this.fetchCheckoutSession(ctx, isDisabled))
        );
  }

  private fetchCheckoutSession(ctx: StateContext<VendorStateModel>, isDisabled: boolean | null): Observable<unknown> {
    const getCheckoutSession$: Observable<CheckoutSession | null> =
      isDisabled === false && ctx.getState().checkoutSessionRequired
        ? this.linkedAccountId$.pipe(
            take(1),
            switchMap(linkedAccountId =>
              this.http.get<CheckoutSession>(PLATFORM_ENVIRONMENT.apiUrl + '/session', {
                params: convertToHttpParams({ linkedAccountId })
              })
            )
          )
        : of(null);

    return getCheckoutSession$.pipe(
      tap(checkoutSession => {
        ctx.patchState({
          checkoutSession: checkoutSession
        });
      }),
      handleErrors(MARKETPLACE_ERROR_HANDLERS),
      handleErrorsUntilSignedOutOnError(this.actions$, () => ({ DEFAULT: new SignOutOnError() }))
    );
  }
}
